import { useCallback, useMemo, useState } from 'react';

import { Table } from '@components/common';
import { getDynamicColumns } from '@components/routes/CmModule/ConfigurationComparison/BranchTable/manageMoBranch';

import useConsistencyCheckHistoryChangedTypes from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryChangedTypes';
import useConsistencyCheckHistoryDetails from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryDetails';
import useConsistencyCheckHistoryManagedObjectIds from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryManagedObjectIds';
import useConsistencyCheckHistoryManagedObjectNames from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryManagedObjectNames';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IMultiDataTableFilter, ISortBy, ITablePagination, ITableSearch, ITableSort, TMultiDataFilterItem, TTableFilter } from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { MIN_FILTER_WIDTH } from '@constants/common';
import { TableFilterTypes } from '@constants/table';

import { getColumns } from './utils';

import styles from './ConsistencyCheckHistoryDetails.module.scss';

interface IConsistencyCheckHistoryDetailsProps {
	dataModelPathId?: string;
	dumpConsistencyId?: string;
}

const ConsistencyCheckHistoryDetails = ({
	dataModelPathId,
	dumpConsistencyId,
}: IConsistencyCheckHistoryDetailsProps) => {
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');

	const [selectedChangedType, setSelectedChangedType] = useState<TSelectOption<string> | null>(null);
	const [multiDataFilter, setMultiDataFilter] = useState<TMultiDataFilterItem[]>([{ moId: null, moName: null }]);

	const {
		consistencyCheckDetails,
		moNames,
		createdAt,
		isLoadingConsistencyCheckHistoryDetails,
		countOfRecords,
	} = useConsistencyCheckHistoryDetails({
		dataModelPathId,
		dumpConsistencyId,
		page,
		pageSize,
		sortDirection: sortFieldName ? sortDirection : undefined,
		sortFieldName: sortFieldName ? sortFieldName.replace('_', '.') : undefined,
		searchValue,
		changedType: selectedChangedType?.value,
		managedObjects: multiDataFilter?.length
			? multiDataFilter
				.filter(option => option.moId?.value && option.moName?.value)
				.map(option => ({
					moId: option.moId?.value,
					moName: option.moName?.value,
				}))
			: undefined,
	});

	const {
		consistencyCheckChangedTypes,
		isLoadingConsistencyCheckChangedTypes,
	} = useConsistencyCheckHistoryChangedTypes({
		dataModelPathId,
		dumpConsistencyId,
	});

	const {
		getConsistencyCheckBranchConfigIdsOptions,
		consistencyCheckManagedObjectIds,
		isLoadingConsistencyCheckManagedObjectIds,
	} = useConsistencyCheckHistoryManagedObjectIds();

	const {
		consistencyCheckManagedObjectNames,
		isLoadingConsistencyCheckManagedObjectNames,
	} = useConsistencyCheckHistoryManagedObjectNames({
		dataModelPathId,
		dumpConsistencyId,
	});

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const dynamicColumns = useMemo(() => getDynamicColumns(moNames), [moNames]);
	const columns = useMemo(() => getColumns(), []);

	const detailColumns = useMemo(() => {
		return dynamicColumns ? [...dynamicColumns, ...columns] : columns;
	}, [columns, dynamicColumns]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: ISortBy[]) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by parameter',
	}], [searchValue, searchHandler]);

	const getSubOptions = useCallback((option: string) => {
		if (!dumpConsistencyId || !dataModelPathId || !option) {
			return;
		}

		getConsistencyCheckBranchConfigIdsOptions({
			dumpConsistencyId,
			dataModelPathId,
			moName: option,
		});
	}, [dumpConsistencyId, dataModelPathId, getConsistencyCheckBranchConfigIdsOptions]);

	const multiDataFilters: IMultiDataTableFilter = {
		selectedFilters: multiDataFilter,
		clearSelectedFilters: () => setMultiDataFilter([{ moId: null, moName: null }]),
		setSelectedFilters: setMultiDataFilter,
		isEmptyFilter: multiDataFilter.every(option => !option.moId?.value && !option.moName?.value),
		filterProps: {
			maxMenuHeight: 200,
			maxWidth: '200px',
			minWidth: '200px',
			placeholder: 'Object',
			subPlaceholder: 'Id',
			name: 'moName',
			subName: 'moId',
			options: consistencyCheckManagedObjectNames
				.filter((o) => !multiDataFilter.find((f) => f.moName?.value === o?.value)),
			subOptions: consistencyCheckManagedObjectIds,
			width: '100%',
			getSubOptions,
		},
	};

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'changedType',
				placeholder: 'Changed type',
				option: selectedChangedType,
				options: consistencyCheckChangedTypes,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: setSelectedChangedType,
				defaultValue: null,
			},
		];
	}, [
		consistencyCheckChangedTypes,
		selectedChangedType,
		setSelectedChangedType,
	]);

	const isLoading = isLoadingConsistencyCheckHistoryDetails
		|| isLoadingConsistencyCheckChangedTypes
		|| isLoadingConsistencyCheckManagedObjectIds
		|| isLoadingConsistencyCheckManagedObjectNames;

	return (
		<div className={styles.wrapper}>
			<Table
				columns={detailColumns}
				customRightAction={createdAt}
				data={consistencyCheckDetails}
				filters={filters}
				isDeleteActionDisabled
				isLoading={isLoading}
				isWithoutActions
				isWithoutCheckbox
				isWithoutDeleteAction
				multiDataFilters={multiDataFilters}
				pagination={pagination}
				search={search}
				sort={sort}
			/>
		</div>
	);
};

export default ConsistencyCheckHistoryDetails;
